<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { refDebounced } from '@vueuse/core'
import ManualCustomerFields from '@/views/quality/audit-collection/ManualCustomerFields.vue'
import QuestionGroupV2 from '@/components/question/QuestionGroupV2.vue'
import AuditCollectionService from '@/services/quality/auditCollection.service'
import DropZone from '@/@core/components/DropZone.vue'
import CapaService from '@/services/quality/capa.service'
import ComplianceScoreCards from '@/views/quality/audit/ComplianceScoreCards.vue'

definePage({
  meta: {
    name: 'incident-audit-collect',
    subject: 'quality.menu',
    action: 'audit_collection',
  },
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const collectionId = ref(route.params.id ?? null)
const auditId = computed(() => route.query.audit_id ?? null)
const unitId = computed(() => cookieRef('selectedUnit').value)

// Dynamic back route based on where user came from
const backRouteName = computed(() => route.meta.navActiveLink ?? 'app-quality-audit-schedule')

// Stepper
const currentStep = ref(0)
const isCurrentStepValid = ref(true)

const stepperItems = [
  { title: 'Audit Details' },
  { title: 'Questionnaire' },
  { title: 'Review & Submit' },
]

// Audit collection data
const auditCollection = ref(null)
const loadingCollection = ref(true)

// Step 1 form
const form = ref({
  location_id: null,
  assessment_type_id: null,
  customer_id: null,
  episode_id: null,
  customer_name: null,
  customer_uhid: null,
  customer_mobile: null,
  episode_no: null,
})

const isCustomerRelated = ref(false)
const refStep1Form = ref()

// Customer API config
const isCustomerApiConfigured = ref(false)

// Manual entry is the only path when no HIS API is configured; when one is
// configured it becomes an escape hatch shown after a search returns nothing.
const isManualEntry = ref(false)
const episodeSearch = ref(null)
const debouncedEpisodeSearch = refDebounced(episodeSearch, 500)
const selectedEpisode = ref(null)

// The resolved patient, kept as an autocomplete item so it can be merged into `items`.
// VAutocomplete can only render a value that exists in its item list, and the live search
// results are replaced on every keystroke — without this pin the field would blank out.
const pinnedEpisode = ref(null)

// Stored UHIDs often already carry the prefix ("UHID-001"), so a literal `UHID ${uhid}`
// reads as "UHID UHID-001". Only prefix values that don't already announce themselves.
const formatUhid = uhid => {
  const value = String(uhid ?? '').trim()

  return /^uhid/i.test(value) ? value : `UHID ${value}`
}

const episodeItemLabel = item => {
  if (!item?.name)
    return ''

  return item.uhid ? `${item.name} · ${formatUhid(item.uhid)}` : item.name
}

// Display label for a resolved patient, used by both modes.
const resolvedCustomerLabel = computed(() =>
  (pinnedEpisode.value ? episodeItemLabel(pinnedEpisode.value) : null))

// The patient can arrive three ways: picked from search, restored from a draft (both pin an
// episode item), or typed manually (sets the form fields, with no record resolved yet).
// The review step has to render all three.
const customerSummary = computed(() => {
  if (resolvedCustomerLabel.value)
    return resolvedCustomerLabel.value

  if (!form.value.customer_name)
    return null

  return form.value.customer_uhid
    ? `${form.value.customer_name} · ${formatUhid(form.value.customer_uhid)}`
    : form.value.customer_name
})

const {
  data: episodeResults,
  isFetching: fetchingEpisodes,
} = useApi(createUrl('/masters/episodes', {
  query: {
    q: debouncedEpisodeSearch,
    limit: 10,
    search: {
      // Bare `unit_id`, not `episodes.unit_id`: ViewEpisodeRequest validates search keys
      // against a whitelist of unqualified columns and rejects the table-prefixed form.
      unit_id: cookieRef('selectedUnit'),
    },
  },
}))

const episodeSearchItems = computed(() => (episodeResults.value ?? []).map(episode => ({
  value: episode.id,
  episode_id: episode.id,
  customer_id: episode.customer?.id ?? episode.customer_id,
  name: episode.customer?.name ?? '—',
  uhid: episode.customer?.uhid ?? null,
  code: episode.code,
  location: episode.location?.name ?? null,
  formType: episode.form_type?.name ?? null,
  visitDate: episode.visit_date_time,
})))

// The pin only applies while the user is NOT actively searching for something else.
// Vuetify copies `item-title` ("name") into the search box on selection, so an untouched
// field reads either as empty (draft restore / manual-save adopt, which pin without
// touching the text) or exactly the pinned item's name. Anything else means the user is
// typing a new query: drop the pin so the results contain only genuine matches and an
// empty result set still reaches the #no-data slot with its "Add manually" action.
const isEpisodeSearchUntouched = computed(() => {
  if (!pinnedEpisode.value)
    return false

  const text = episodeSearch.value ?? ''

  return text === '' || text === (pinnedEpisode.value.name ?? '')
})

// While untouched the pinned selection is first and de-duplicated against the live results,
// so the field keeps rendering it no matter what the current search returned.
const episodeItems = computed(() => {
  if (!isEpisodeSearchUntouched.value)
    return episodeSearchItems.value

  return [
    pinnedEpisode.value,
    ...episodeSearchItems.value.filter(item => item.value !== pinnedEpisode.value.value),
  ]
})

// A search is "pending" while the request is in flight, or while the debounce
// has not yet caught up with what the user just typed. Both cases must suppress
// the "no matching record" message so it never flashes mid-keystroke.
const isEpisodeSearchPending = computed(() =>
  fetchingEpisodes.value
  || (episodeSearch.value ?? '') !== (debouncedEpisodeSearch.value ?? ''))

// Drives the autocomplete's #no-data slot:
// 'idle'     - no query typed yet, prompt the user to search
// 'pending'  - request in flight / debounce settling
// 'no-match' - a real query returned nothing, offer manual entry
const episodeNoDataState = computed(() => {
  if (!debouncedEpisodeSearch.value && !episodeSearch.value)
    return 'idle'

  if (isEpisodeSearchPending.value)
    return 'pending'

  return 'no-match'
})

// Pins a resolved patient as the autocomplete's value. `episodeId` may be null (a customer
// with no episode), so the item gets a synthetic key in that case — `episode_id` stays the
// real value that goes onto the form.
const pinResolvedCustomer = ({ customerId, episodeId, name, uhid }) => {
  pinnedEpisode.value = {
    value: episodeId ?? `customer:${customerId}`,
    episode_id: episodeId ?? null,
    customer_id: customerId,
    name: name ?? '—',
    uhid: uhid ?? null,
    code: null,
    location: null,
    formType: null,
    visitDate: null,
  }

  selectedEpisode.value = pinnedEpisode.value.value
}

const onEpisodeSelected = episodeId => {
  // The clearable X emits null — never leave a stale customer on the form.
  if (isNullOrUndefined(episodeId)) {
    clearResolvedCustomer()

    return
  }

  // Resolve against the live results plus the current pin directly, not `episodeItems`:
  // that computed drops the pin as soon as the search text moves away from it, and Vuetify
  // may already have rewritten the text by the time this handler runs.
  const picked = episodeSearchItems.value.find(item => item.value === episodeId)
    ?? (pinnedEpisode.value?.value === episodeId ? pinnedEpisode.value : null)

  if (!picked)
    return

  form.value.customer_id = picked.customer_id
  form.value.episode_id = picked.episode_id
  pinResolvedCustomer({
    customerId: picked.customer_id,
    episodeId: picked.episode_id,
    name: picked.name,
    uhid: picked.uhid,
  })
}

const startManualEntry = () => {
  isManualEntry.value = true
  form.value.customer_name = episodeSearch.value ?? null
}

const clearResolvedCustomer = () => {
  form.value.customer_id = null
  form.value.episode_id = null
  form.value.customer_name = null
  form.value.customer_uhid = null
  form.value.customer_mobile = null
  form.value.episode_no = null
  pinnedEpisode.value = null
  selectedEpisode.value = null
  episodeSearch.value = null
  isManualEntry.value = false
}

// After a save the server may have created a Customer + Episode from the typed fields.
// Adopt them so the UI shows a resolved patient and the next save doesn't re-send stale
// manual input that the backend would discard.
const adoptResolvedCustomer = data => {
  if (!data?.customer_id)
    return

  form.value.customer_id = data.customer_id
  form.value.episode_id = data.episode_id ?? null
  form.value.customer_name = null
  form.value.customer_uhid = null
  form.value.customer_mobile = null
  form.value.episode_no = null
  isManualEntry.value = false

  pinResolvedCustomer({
    customerId: data.customer_id,
    episodeId: data.episode_id ?? null,
    name: data.customer?.name ?? null,
    uhid: data.customer?.uhid ?? null,
  })
}

// Location data
const customerLocation = ref(null)

// Fallback location list — only fetched when the audit configures no locations for this unit
const {
  data: locationsData,
  isFetching: fetchingLocations,
  execute: fetchAllLocations,
} = useApi(createUrl('/masters/locations', {
  query: {
    search: {
      unit_id: unitId,
    },
    select: ['id', 'name'],
  },
}), { immediate: false })

// Locations configured on the audit, narrowed to the collector's unit
const configuredLocations = computed(() =>
  (auditCollection.value?.audit?.locations ?? []).filter(l => l.unit_id === unitId.value))

const locations = computed(() => {
  const base = configuredLocations.value.length > 0
    ? configuredLocations.value
    : (locationsData.value?.data ?? locationsData.value ?? [])

  // Keep a draft's saved location selectable even if it was later removed from the audit config
  const saved = auditCollection.value?.location
  if (saved && !base.some(l => l.id === saved.id))
    return [saved, ...base]

  return base
})

// Assessment types from audit
const assessmentTypes = computed(() => auditCollection.value?.audit?.assessment_types ?? [])

// Check customer API status
await useApi(createUrl('/configurations/customer-api-status', {
  query: {
    unit_id: cookieRef('selectedUnit'),
  },
}), {
  afterFetch: response => {
    isCustomerApiConfigured.value = response?.data?.status
  },
})

// Fetch existing audit collection (edit/continue mode)
if (collectionId.value) {
  await useApi(createUrl(`/incident/audit-collections/${collectionId.value}`, {
    query: {
      with: ['audit.assessmentTypes', 'questionAnswers.incidentQuestionOption', 'questionAnswers.incidentQuestion'],
    },
  }), {
    afterFetch: ({ data }) => {
      auditCollection.value = data

      // Restore draft data
      if (data.assessment_type_id)
        form.value.assessment_type_id = data.assessment_type_id

      if (data.location_id)
        form.value.location_id = data.location_id

      if (data.episode_id)
        form.value.episode_id = data.episode_id

      if (data.customer_id) {
        form.value.customer_id = data.customer_id
        isCustomerRelated.value = true

        // No search has run yet, so the draft's patient is pinned straight into the
        // autocomplete's item list — otherwise the field would have nothing to render.
        pinResolvedCustomer({
          customerId: data.customer_id,
          episodeId: data.episode_id ?? null,
          name: data.customer?.name ?? null,
          uhid: data.customer?.uhid ?? null,
        })
      }

      loadingCollection.value = false
    },
    onFetchError: () => {
      loadingCollection.value = false
      router.replace({
        name: 'invalid-url',
        params: { code: 404 },
      })
    },
  })
}

// Fetch audit data for new collection (ad-hoc start mode)
if (!collectionId.value && auditId.value) {
  await useApi(createUrl(`/incident/audits/${auditId.value}`, {
    query: {
      with: ['assessmentTypes'],
    },
  }), {
    afterFetch: ({ data }) => {
      // Shape it like an audit collection so assessmentTypes computed works
      auditCollection.value = {
        audit_id: data.id,
        audit: data,
      }
      loadingCollection.value = false
    },
    onFetchError: () => {
      loadingCollection.value = false
      router.replace({
        name: 'invalid-url',
        params: { code: 404 },
      })
    },
  })
}

// Fall back to all unit locations only when the audit configures none for this unit
if (!configuredLocations.value.length)
  await fetchAllLocations()

// Customer toggle watcher
watch(isCustomerRelated, newVal => {
  if (!newVal) {
    clearResolvedCustomer()
    customerLocation.value = null
  }
})

// ─── Step 2: Questionnaire ───

// Answer state
const answers = ref({})
const reasons = ref({})
const groupVisibility = ref({})
const questionSelections = ref({})

// Attachment state is split in three, because VFileInput can only render File instances:
//   answers[qid]              -> ONLY newly picked File[] (what VFileInput is bound to)
//   persistedAttachments[qid] -> [{ uuid, url, name, mime_type }] already on the server
//   deletedAttachmentUuids[qid] -> uuids the user removed but has not yet saved
const persistedAttachments = ref({})
const deletedAttachmentUuids = ref({})

// Reactive assessment type ID for question groups query
const assessmentTypeId = computed(() => form.value.assessment_type_id)

const {
  data: questionGroupsData,
  isFetching: loadingQuestions,
} = useApi(createUrl('/incident/question-groups', {
  query: {
    search: {
      assessment_type_id: assessmentTypeId,
    },
    with: ['questions.questionOptions'],
  },
}))

const questionGroups = computed(() => {
  const raw = questionGroupsData.value
  if (!raw) return []

  const items = Array.isArray(raw) ? raw : (raw?.data ?? [])

  return items
    .sort((a, b) => a.order - b.order)
    .map(group => ({
      ...group,
      questions: (group.questions ?? []).sort((a, b) => a.order - b.order),
    }))
})

// Restore draft answers once question groups have loaded
watch(questionGroups, groups => {
  if (groups.length > 0 && auditCollection.value?.question_answers?.length) {
    nextTick(() => {
      restoreAnswersFromDraft(auditCollection.value.question_answers)
    })
  }
}, { once: true })

// Clear answers when assessment type changes
watch(assessmentTypeId, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal && oldVal !== null) {
    answers.value = {}
    reasons.value = {}
    groupVisibility.value = {}
    questionSelections.value = {}
    persistedAttachments.value = {}
    deletedAttachmentUuids.value = {}
  }
})

// Resolve the comparable scalar from an answer (option object, or raw primitive)
function resolveAnswerValue(answer) {
  return answer?.value ?? answer?.id ?? answer
}

// Does an answer value satisfy a sub-question's parent conditions?
// String() coercion is deliberate: option ids arrive as numbers but may be
// compared against string values. Survey's raw .includes() misses these.
function matchesParentConditions(conditions, answerValue) {
  if (!conditions?.length) return false
  if (answerValue === null || answerValue === undefined) return false

  return conditions.some(condition => String(condition) === String(answerValue))
}

const visibleQuestionIdsByGroup = computed(() => {
  const result = {}

  for (const group of questionGroups.value) {
    const visibleIds = []
    const allQuestions = group.questions || []
    const rootQuestions = allQuestions.filter(q => !q.parent_question_id)

    for (const question of rootQuestions) {
      if (groupVisibility.value[group.id] === false)
        continue

      if (group.can_have_question_selection) {
        const selected = questionSelections.value[group.id] || []
        if (!selected.includes(question.id))
          continue
      }

      visibleIds.push(question.id)

      const answerValue = resolveAnswerValue(answers.value[question.id])

      const children = allQuestions.filter(q => q.parent_question_id === question.id)

      for (const child of children) {
        if (matchesParentConditions(child.parent_question_conditions, answerValue))
          visibleIds.push(child.id)
      }
    }

    result[group.id] = visibleIds
  }

  return result
})

function getAllQuestions() {
  const result = []

  for (const group of questionGroups.value) {
    for (const question of group.questions || [])
      result.push(question)
  }

  return result
}

// Drop a question's state. By default this removes ALL four maps — used when a question is
// genuinely gone (group marked not-applicable, or a question deselected): its media should
// cascade-delete on the next save.
//
// With { keepMedia: true } only the live `answers`/`reasons` are dropped, while
// `persistedAttachments` and `deletedAttachmentUuids` are LEFT INTACT. This is the answer-toggle
// path: hiding a sub-question by flipping its parent must not submit a stale answer, but it must
// also NOT destroy attachments the user hasn't committed to removing. Keeping the media state
// keeps the question in `responses` (see buildPayload's hasMediaState union) with an empty
// `response`, which the backend reads as KEEP — so a mis-click corrected before saving loses
// nothing, and toggling back restores the question fully.
function purgeQuestionState(questionIds, { keepMedia = false } = {}) {
  if (!questionIds.length) return

  const newAnswers = { ...answers.value }
  const newReasons = { ...reasons.value }

  questionIds.forEach(id => {
    delete newAnswers[id]
    delete newReasons[id]
  })

  answers.value = newAnswers
  reasons.value = newReasons

  if (keepMedia) return

  const newPersisted = { ...persistedAttachments.value }
  const newDeletions = { ...deletedAttachmentUuids.value }

  questionIds.forEach(id => {
    delete newPersisted[id]
    delete newDeletions[id]
  })

  persistedAttachments.value = newPersisted
  deletedAttachmentUuids.value = newDeletions
}

function clearHiddenSubQuestionAnswers(parentQuestionId) {
  const parentId = Number(parentQuestionId)
  const children = getAllQuestions().filter(q => q.parent_question_id === parentId)

  if (!children.length) return

  const answerValue = resolveAnswerValue(answers.value[parentId])

  // keepMedia: this is a mere answer toggle, not a removal — preserve any attachments the user
  // hasn't explicitly deleted so a mis-click (No -> Yes -> No) never cascade-deletes evidence.
  purgeQuestionState(
    children
      .filter(child => !matchesParentConditions(child.parent_question_conditions, answerValue))
      .map(child => child.id),
    { keepMedia: true },
  )
}

function handleAnswerUpdate({ questionId, value, reason }) {
  if (value !== undefined) {
    answers.value = { ...answers.value, [questionId]: value }
    clearHiddenSubQuestionAnswers(questionId)
  }

  if (reason !== undefined)
    reasons.value = { ...reasons.value, [questionId]: reason }
}

function handleGroupVisibilityChange({ groupId, visible }) {
  groupVisibility.value = { ...groupVisibility.value, [groupId]: visible }

  if (visible) return

  const group = questionGroups.value.find(g => g.id === groupId)

  purgeQuestionState((group?.questions ?? []).map(q => q.id))
}

function handleQuestionSelectionChange({ groupId, selectedIds }) {
  questionSelections.value = { ...questionSelections.value, [groupId]: selectedIds }

  const group = questionGroups.value.find(g => g.id === groupId)

  if (!group) return

  purgeQuestionState(
    (group.questions ?? [])
      .filter(q => !selectedIds.includes(q.id) && !selectedIds.includes(q.parent_question_id))
      .map(q => q.id),
  )
}

// Closing a persisted-attachment chip: drop it from view and queue its uuid for detach.
// Absence of a uuid from deleted_attachment_uuids means KEEP, so removal must be explicit.
function handleRemovePersistedAttachment({ questionId, uuid }) {
  persistedAttachments.value = {
    ...persistedAttachments.value,
    [questionId]: (persistedAttachments.value[questionId] ?? []).filter(a => a.uuid !== uuid),
  }

  const queued = deletedAttachmentUuids.value[questionId] ?? []

  if (queued.includes(uuid)) return

  deletedAttachmentUuids.value = {
    ...deletedAttachmentUuids.value,
    [questionId]: [...queued, uuid],
  }
}

// ─── Compliance Score ───

function normalizeOptionValue(val) {
  if (val === null || val === undefined || val === '') return NaN
  if (typeof val === 'string') {
    const lower = val.toLowerCase()
    if (lower === 'yes') return 1
    if (lower === 'no') return 0
  }

  return Number(val)
}

const complianceQuestions = computed(() => {
  const allQuestions = getAllQuestions()

  return allQuestions.filter(q => {
    if (!q.is_compliance || q.is_compliance == 0) return false

    const options = q.question_options ?? []
    if (options.length === 0) return false

    return options.every(opt => !isNaN(normalizeOptionValue(opt.value)))
  })
})

const avgMaxOptionValue = computed(() => {
  const questions = complianceQuestions.value
  if (questions.length === 0) return 0

  let sumOfMax = 0

  questions.forEach(q => {
    const options = q.question_options ?? []

    sumOfMax += Math.max(...options.map(opt => normalizeOptionValue(opt.value)))
  })

  return Math.round((sumOfMax / questions.length) * 100) / 100
})

const totalScore = computed(() => complianceQuestions.value.length * avgMaxOptionValue.value)

const complianceScore = computed(() => {
  let score = 0

  complianceQuestions.value.forEach(q => {
    const answer = answers.value[q.id]

    if (answer !== null && answer !== undefined) {
      const val = normalizeOptionValue(q.question_options.find(opt => opt.id === answer.value)?.value)
      if (!isNaN(val)) {
        score += val
      }
    }
  })

  return score
})

const complianceScoreFormula = computed(() => {
  const values = complianceQuestions.value
    .map(q => {
      const answer = answers.value[q.id]
      if (answer !== null && answer !== undefined) {
        const val = normalizeOptionValue(q.question_options.find(opt => opt.id === answer.value)?.value)
        if (!isNaN(val)) return val
      }

      return null
    })
    .filter(v => v !== null)

  return values.length > 0 ? values.join(' + ') : ''
})

const compliancePercentage = computed(() => {
  if (totalScore.value === 0) return 0

  return Math.round((complianceScore.value / totalScore.value) * 100)
})

const hasComplianceQuestions = computed(() => complianceQuestions.value.length > 0)

// Draft restoration
function restoreAnswersFromDraft(questionAnswers) {
  const restored = {}
  const restoredReasons = {}
  const restoredPersisted = {}

  questionAnswers.forEach(qa => {
    const questionId = qa.incident_question_id
    const questionType = qa.incident_questions?.question_type

    if (questionType === 'attachments') {
      // Server media goes to the chip row; answers[] starts empty and collects only new Files.
      restoredPersisted[questionId] = (qa.attachments ?? []).map(a => ({
        uuid: a.uuid,
        url: a.url,
        name: a.name,
        mime_type: a.mime_type,
      }))
      restored[questionId] = []

      return
    }

    if (qa.incident_question_option_id && qa.question_option) {
      const option = { value: qa.question_option.id, label: qa.question_option.label }

      if (questionType === 'checkbox') {
        if (!restored[questionId]) restored[questionId] = []
        restored[questionId].push(option)
      }
      else {
        restored[questionId] = option
      }
    }
    else if (qa.response !== null && qa.response !== undefined) {
      restored[questionId] = qa.response
    }

    if (qa.reason_response) {
      restoredReasons[questionId] = qa.reason_response
    }
  })

  answers.value = restored
  reasons.value = restoredReasons
  persistedAttachments.value = restoredPersisted
  deletedAttachmentUuids.value = {}
}

// ─── Step 3: Preview ───

const getResponseValue = response => {
  if (Array.isArray(response))
    return response.map(item => item?.label ?? item?.value ?? item).join(', ')

  return response?.label ?? response?.value ?? response
}

// Media for an attachments question, in the shape AppQAAttachments renders:
// already-saved media plus any File the user just picked. Newly picked Files have no
// server URL yet, so they get a blob URL purely for this preview.
function previewAttachmentsFor(question) {
  if (question.question_type !== 'attachments') return []

  const persisted = (persistedAttachments.value[question.id] ?? []).map(a => ({
    id: a.uuid,
    uuid: a.uuid,
    url: a.url,
    name: a.name,
    mime_type: a.mime_type,
  }))

  const picked = (Array.isArray(answers.value[question.id]) ? answers.value[question.id] : [])
    .filter(file => file instanceof File)
    .map((file, index) => ({
      id: `new-${question.id}-${index}`,
      url: URL.createObjectURL(file),
      name: file.name,
      mime_type: file.type,
      size: file.size,
    }))

  return [...persisted, ...picked]
}

function buildPreviewItem(question) {
  const value = answers.value[question.id]

  let answer = ''
  let attachment = null

  if (question.question_type === 'ai_voice' && value)
    attachment = value instanceof File ? URL.createObjectURL(value) : value
  else if (question.question_type === 'smile')
    answer = value?.label ?? ''
  else if (question.question_type !== 'attachments' && value !== null && value !== undefined && value !== '')
    answer = Array.isArray(value) && !value.length ? '' : getResponseValue(value)

  return {
    question: question.label,
    answer,
    question_type: question.question_type,
    answer_reason: reasons.value[question.id] || null,
    attachment,
    attachments: previewAttachmentsFor(question),
  }
}

const hasPreviewContent = item =>
  Boolean(item.answer || item.attachment || item.attachments?.length)

// Built from the question tree rather than from `answers`, mirroring view.vue's
// groupedQaItems so Review and the read-only view read identically. Driving from the
// tree is what makes an attachments answer visible at all: once its media is persisted
// `answers[id]` is an empty array, which the old key-iteration dropped outright.
// Only questions the user actually saw are included, so a hidden sub-question or a
// not-applicable group never reaches the review.
const previewItems = computed(() => {
  const items = []

  for (const group of questionGroups.value) {
    const questions = group.questions ?? []
    const visibleIds = visibleQuestionIdsByGroup.value[group.id] ?? []

    const roots = questions.filter(q => !q.parent_question_id && visibleIds.includes(q.id))

    for (const root of roots) {
      const item = buildPreviewItem(root)

      item.sub_items = questions
        .filter(q => q.parent_question_id === root.id && visibleIds.includes(q.id))
        .map(buildPreviewItem)
        .filter(hasPreviewContent)

      if (hasPreviewContent(item) || item.sub_items.length)
        items.push(item)
    }
  }

  return items
})

// ─── Draft Save & Submit ───

const savingDraft = ref(false)
const submitting = ref(false)

// CAPA Dialog
const isCapaDialogOpen = ref(false)
const submittingWithCapa = ref(false)

const capaForm = ref({
  corrective_action: '',
  preventive_action: '',
  preventive_action_due_date: null,
  corrective_action_attachments: [],
  preventive_action_attachments: [],
})

const resetCapaForm = () => {
  capaForm.value = {
    corrective_action: '',
    preventive_action: '',
    preventive_action_due_date: null,
    corrective_action_attachments: [],
    preventive_action_attachments: [],
  }
}

const isBundleCompliance = computed(() => auditCollection.value?.audit?.type === 'BUNDLE_COMPLIANCE')

const isNonCompliant = computed(() => {
  return isBundleCompliance.value && hasComplianceQuestions.value && compliancePercentage.value < 100
})

const failedElements = computed(() => {
  return complianceQuestions.value
    .filter(q => {
      const answer = answers.value[q.id]
      if (!answer) return true

      const options = q.question_options ?? []
      const maxValue = Math.max(...options.map(opt => normalizeOptionValue(opt.value)))
      const selectedValue = normalizeOptionValue(options.find(opt => opt.id === answer.value)?.value ?? 0)

      return selectedValue < maxValue
    })
    .map(q => q.label)
})

const refCapaForm = ref()

const openCapaDialog = () => {
  resetCapaForm()
  isCapaDialogOpen.value = true
}

const submitWithCapa = async () => {
  const { valid } = await refCapaForm.value.validate()
  if (!valid) return

  submittingWithCapa.value = true

  await saveOrCreate(
    buildPayload('SUBMITTED'),
    async () => {
      const capaData = {
        subject_type: 'AuditCollection',
        subject_id: collectionId.value,
        corrective_action: capaForm.value.corrective_action,
        preventive_action: capaForm.value.preventive_action || null,
        preventive_action_due_date: capaForm.value.preventive_action_due_date || null,
        corrective_action_attachments: capaForm.value.corrective_action_attachments,
        preventive_action_attachments: capaForm.value.preventive_action_attachments,
      }

      await CapaService.store(
        capaData,
        () => {
          submittingWithCapa.value = false
          isCapaDialogOpen.value = false
          router.push({ name: backRouteName.value })
        },
        () => {
          submittingWithCapa.value = false
        },
      )
    },
    () => {
      submittingWithCapa.value = false
    },
  )
}

const buildPayload = status => {
  const attachmentQuestionIds = new Set(
    getAllQuestions().filter(q => q.question_type === 'attachments').map(q => q.id),
  )

  const isAttachment = questionId => attachmentQuestionIds.has(Number(questionId))

  const newFilesFor = questionId => (answers.value[questionId] ?? []).filter(item => item instanceof File)
  const persistedFor = questionId => persistedAttachments.value[questionId] ?? []
  const deletionsFor = questionId => deletedAttachmentUuids.value[questionId] ?? []

  // An attachments question must ride along whenever it holds ANY media state — new files,
  // persisted media, or pending deletions. The backend deletes answer rows for questions absent
  // from `responses`, and that delete cascades to MediaLibrary: dropping a question whose media
  // is merely persisted-and-untouched would permanently destroy those files from disk.
  const hasMediaState = questionId =>
    newFilesFor(questionId).length > 0 ||
    persistedFor(questionId).length > 0 ||
    deletionsFor(questionId).length > 0

  // Union in attachments questions carrying media state even if `answers` lost its key, so a
  // missed key can never silently cascade-delete files. purgeQuestionState clears all three
  // maps together, so an intentionally removed question never reappears here.
  const questionIds = [...new Set([
    ...Object.keys(answers.value),
    ...[...attachmentQuestionIds].filter(hasMediaState).map(String),
  ])]

  const responses = questionIds
    .filter(questionId => {
      if (isAttachment(questionId)) return hasMediaState(questionId)

      const value = answers.value[questionId]

      if (value === null || value === undefined || value === '') return false

      return !Array.isArray(value) || value.length > 0
    })
    .map(questionId => {
      if (isAttachment(questionId)) {
        return {
          question_id: Number(questionId),

          // Only NEW files go over the wire — after a reload we hold metadata, not bytes.
          // An absent uuid means KEEP, so persisted media is never re-sent.
          response: newFilesFor(questionId),
          deleted_attachment_uuids: deletionsFor(questionId),
        }
      }

      return {
        question_id: Number(questionId),
        response: answers.value[questionId],
        reason_response: reasons.value[questionId] || null,
      }
    })

  return {
    audit_id: auditCollection.value?.audit_id,
    unit_id: unitId.value,
    status,
    location_id: form.value.location_id,
    assessment_type_id: form.value.assessment_type_id,

    // '' not null: convertToFormData drops nulls, so a null FK would never reach the server
    // and the previously-attached patient could never be changed or detached. Laravel's
    // ConvertEmptyStringsToNull turns '' back into a present null key.
    customer_id: form.value.customer_id ?? '',
    episode_id: form.value.episode_id ?? '',
    customer_name: form.value.customer_name,
    customer_uhid: form.value.customer_uhid,
    customer_mobile: form.value.customer_mobile,
    episode_no: form.value.episode_no,
    responses,
  }
}

// After a save the server returns the authoritative media. Move the Files we just uploaded over
// to persistedAttachments as {uuid,...} metadata and empty answers[], so the next save neither
// re-uploads them nor re-sends deletions that already went through.
function syncAttachmentsFromResponse(data) {
  if (!data?.question_answers?.length) return

  const nextAnswers = { ...answers.value }
  const nextPersisted = { ...persistedAttachments.value }

  data.question_answers.forEach(qa => {
    if (qa.incident_questions?.question_type !== 'attachments') return

    nextPersisted[qa.incident_question_id] = (qa.attachments ?? []).map(a => ({
      uuid: a.uuid,
      url: a.url,
      name: a.name,
      mime_type: a.mime_type,
    }))
    nextAnswers[qa.incident_question_id] = []
  })

  answers.value = nextAnswers
  persistedAttachments.value = nextPersisted
  deletedAttachmentUuids.value = {}
}

const saveOrCreate = async (payload, onSuccess, onError) => {
  if (collectionId.value) {
    await AuditCollectionService.update(collectionId.value, payload, data => {
      syncAttachmentsFromResponse(data)
      adoptResolvedCustomer(data)
      onSuccess(data)
    }, onError)
  }
  else {
    await AuditCollectionService.create(payload, data => {
      syncAttachmentsFromResponse(data)
      adoptResolvedCustomer(data)
      collectionId.value = data.id
      router.replace({
        name: 'app-quality-audit-collect',
        params: { id: data.id },
      })
      onSuccess(data)
    }, onError)
  }
}

const saveDraft = async () => {
  savingDraft.value = true

  let success = false

  await saveOrCreate(
    buildPayload('DRAFT'),
    () => {
      savingDraft.value = false
      success = true
    },
    () => {
      savingDraft.value = false
      success = false
    },
  )

  return success
}

// Step validation
const validateStep1 = async () => {
  const { valid } = await refStep1Form.value.validate()
  if (!valid) {
    isCurrentStepValid.value = false

    return
  }

  isCurrentStepValid.value = true

  const saved = await saveDraft()
  if (!saved) return

  currentStep.value++
}

const validateStep2 = async () => {
  isCurrentStepValid.value = true

  const saved = await saveDraft()
  if (!saved) return

  currentStep.value++
}

const saveAsDraft = async () => {
  savingDraft.value = true

  await saveOrCreate(
    buildPayload('DRAFT'),
    () => {
      savingDraft.value = false
      router.push({ name: backRouteName.value })
    },
    () => {
      savingDraft.value = false
    },
  )
}

const submitAudit = async () => {
  submitting.value = true

  await saveOrCreate(
    buildPayload('SUBMITTED'),
    () => {
      submitting.value = false
      router.push({ name: backRouteName.value })
    },
    () => {
      submitting.value = false
    },
  )
}

// Selected assessment type name for display
const selectedAssessmentTypeName = computed(() => {
  return assessmentTypes.value.find(t => t.id === form.value.assessment_type_id)?.name ?? ''
})

const selectedLocationName = computed(() => {
  if (customerLocation.value?.name)
    return customerLocation.value.name

  return locations.value.find(l => l.id === form.value.location_id)?.name ?? ''
})
</script>

<template>
  <section v-if="!loadingCollection">
    <!-- Page Header + Stepper -->
    <div class="d-flex align-center justify-space-between flex-wrap gap-4 mb-6">
      <div class="d-flex align-center gap-4">
        <IconBtn
          variant="tonal"
          class="rounded"
          size="large"
          @click="router.push({ name: backRouteName.value })"
        >
          <VIcon
            size="24"
            icon="tabler-arrow-left"
          />
        </IconBtn>
        <div>
          <div class="text-h5">
            Audit Collection
          </div>
          <span class="text-body-2 text-medium-emphasis">
            {{ auditCollection?.audit?.title ?? 'Perform the audit questionnaire' }}
          </span>
        </div>
      </div>

      <AppStepper
        v-model:current-step="currentStep"
        :items="stepperItems"
        :is-active-step-valid="isCurrentStepValid"
      />
    </div>

    <!-- Step Windows -->
    <VWindow
      v-model="currentStep"
      :touch="false"
      class="disable-tab-transition"
    >
      <!-- STEP 1: Audit Details -->
      <VWindowItem>
        <VForm
          ref="refStep1Form"
          @submit.prevent="validateStep1"
        >
          <VCard
            variant="flat"
            class="border"
          >
            <VCardItem>
              <template #title>
                <span class="text-h6 font-weight-medium">Audit Details</span>
              </template>
              <template #subtitle>
                <span class="text-body-2 text-medium-emphasis">
                  Configure the audit parameters
                </span>
              </template>
            </VCardItem>
            <VDivider />
            <VCardText>
              <VRow>
                <!-- Customer Related Toggle -->
                <VCol
                  cols="12"
                  md="12"
                >
                  <VLabel class="mb-1 text-body-2 text-high-emphasis">
                    {{ `Is this ${t('Customer').toLowerCase()} related audit?` }}
                  </VLabel>
                  <div>
                    <VBtnToggle
                      v-model="isCustomerRelated"
                      mandatory
                      color="primary"
                      variant="tonal"
                      density="compact"
                      class="mt-1"
                    >
                      <VBtn :value="true">
                        Yes
                      </VBtn>
                      <VBtn :value="false">
                        No
                      </VBtn>
                    </VBtnToggle>
                  </div>
                </VCol>

                <!-- Resolved patient (no HIS search field exists in this mode to hold it) -->
                <VCol
                  v-if="isCustomerRelated && form.customer_id && !isCustomerApiConfigured"
                  cols="12"
                >
                  <VAlert
                    variant="tonal"
                    color="success"
                    class="mb-0"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <span class="font-weight-medium">{{ resolvedCustomerLabel }}</span>
                      <VBtn
                        size="small"
                        variant="text"
                        @click="clearResolvedCustomer"
                      >
                        Change
                      </VBtn>
                    </div>
                  </VAlert>
                </VCol>

                <!-- Episode search (HIS configured); holds the selection as its own value -->
                <VCol
                  v-if="isCustomerRelated && isCustomerApiConfigured && !isManualEntry"
                  cols="12"
                >
                  <AppAutocomplete
                    v-model="selectedEpisode"
                    v-model:search="episodeSearch"
                    :items="episodeItems"
                    :loading="fetchingEpisodes"
                    item-title="name"
                    item-value="value"
                    :label="`${$t('Customer')} / Episode`"
                    placeholder="Search by name, UHID, episode no or mobile"
                    no-filter
                    clearable
                    @update:model-value="onEpisodeSelected"
                    @click:clear="clearResolvedCustomer"
                  >
                    <template #selection="{ item }">
                      <span class="text-body-1">{{ episodeItemLabel(item.raw) }}</span>
                    </template>

                    <template #item="{ props: itemProps, item }">
                      <VListItem v-bind="itemProps">
                        <template #title>
                          <span class="font-weight-medium">{{ item.raw.name }}</span>
                          <span
                            v-if="item.raw.uhid"
                            class="text-body-2 text-medium-emphasis"
                          >
                            · {{ formatUhid(item.raw.uhid) }}
                          </span>
                          <span
                            v-if="item.raw.code"
                            class="text-body-2 text-medium-emphasis"
                          >
                            · {{ item.raw.code }}
                          </span>
                        </template>
                        <template #subtitle>
                          <span class="text-body-2 text-medium-emphasis">
                            {{ [item.raw.formType, item.raw.visitDate ? formatDate(item.raw.visitDate) : null, item.raw.location].filter(Boolean).join(' · ') }}
                          </span>
                        </template>
                      </VListItem>
                    </template>

                    <template #no-data>
                      <VListItem v-if="episodeNoDataState === 'idle'">
                        <span class="text-body-2 text-medium-emphasis">
                          Start typing to search
                        </span>
                      </VListItem>

                      <VListItem v-else-if="episodeNoDataState === 'pending'">
                        <div class="d-flex align-center gap-2">
                          <VProgressCircular
                            indeterminate
                            size="16"
                            width="2"
                            color="primary"
                          />
                          <span class="text-body-2 text-medium-emphasis">
                            Searching…
                          </span>
                        </div>
                      </VListItem>

                      <template v-else>
                        <VListItem>
                          <span class="text-body-2 text-medium-emphasis">
                            No matching record found
                          </span>
                        </VListItem>

                        <VDivider />

                        <VListItem
                          link
                          class="mt-3 py-3"
                          @mousedown.prevent
                          @click="startManualEntry"
                        >
                          <template #prepend>
                            <VIcon
                              icon="tabler-user-plus"
                              size="20"
                              color="primary"
                              class="me-3"
                            />
                          </template>
                          <template #title>
                            <span class="text-body-2 font-weight-medium text-primary">
                              Add manually
                            </span>
                          </template>
                          <template #subtitle>
                            <span class="text-body-2 text-medium-emphasis">
                              Enter details for “{{ debouncedEpisodeSearch }}”
                            </span>
                          </template>
                        </VListItem>
                      </template>
                    </template>
                  </AppAutocomplete>
                </VCol>

                <!-- Manual entry -->
                <VCol
                  v-if="isCustomerRelated && !form.customer_id && (!isCustomerApiConfigured || isManualEntry)"
                  cols="12"
                >
                  <ManualCustomerFields v-model="form" />

                  <VBtn
                    v-if="isManualEntry"
                    size="small"
                    variant="outlined"
                    class="mt-2"
                    @click="clearResolvedCustomer"
                  >
                    Back to search
                  </VBtn>
                </VCol>

                <!-- Location -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppSelect
                    v-model="form.location_id"
                    :items="locations"
                    :item-title="item => item.name"
                    :item-value="item => item.id"
                    label="Location"
                    placeholder="Select location"
                    :rules="[requiredValidator]"
                    required
                    :loading="fetchingLocations"
                  />
                </VCol>

                <!-- Assessment Type -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppSelect
                    v-model="form.assessment_type_id"
                    :items="assessmentTypes"
                    :item-title="item => item.name"
                    :item-value="item => item.id"
                    label="Assessment Type"
                    placeholder="Select assessment type"
                    :rules="[requiredValidator]"
                    required
                  />
                </VCol>
              </VRow>
            </VCardText>
          </VCard>

          <!-- Navigation -->
          <VCard
            class="mt-6 border"
            variant="flat"
          >
            <VCardText>
              <div class="d-flex justify-space-between">
                <VBtn
                  color="secondary"
                  variant="tonal"
                  disabled
                >
                  <VIcon
                    icon="tabler-arrow-left"
                    start
                    class="flip-in-rtl"
                  />
                  Previous
                </VBtn>
                <VBtn
                  color="primary"
                  type="submit"
                  :loading="savingDraft"
                  :disabled="savingDraft"
                >
                  Next
                  <VIcon
                    icon="tabler-arrow-right"
                    end
                    class="flip-in-rtl"
                  />
                </VBtn>
              </div>
            </VCardText>
          </VCard>
        </VForm>
      </VWindowItem>

      <!-- STEP 2: Questionnaire -->
      <VWindowItem>
        <VCard
          variant="flat"
          class="border"
        >
          <VCardItem>
            <template #title>
              <span class="text-h6 font-weight-medium">Questionnaire</span>
            </template>
            <template #subtitle>
              <span class="text-body-2 text-medium-emphasis">
                Answer the audit questions for {{ selectedAssessmentTypeName }}
              </span>
            </template>
          </VCardItem>
          <VDivider />
          <VCardText>
            <template v-if="!loadingQuestions">
              <div class="d-flex flex-column gap-4">
                <QuestionGroupV2
                  v-for="group in questionGroups"
                  :key="group.id"
                  :question-group="group"
                  :answers="answers"
                  :reasons="reasons"
                  :visible-question-ids="visibleQuestionIdsByGroup[group.id] || []"
                  :group-visible="groupVisibility[group.id] !== false"
                  :selected-question-ids="questionSelections[group.id] || []"
                  :persisted-attachments="persistedAttachments"
                  :disabled="false"
                  @update:answer="handleAnswerUpdate"
                  @update:group-visibility="handleGroupVisibilityChange"
                  @update:question-selection="handleQuestionSelectionChange"
                  @remove-persisted-attachment="handleRemovePersistedAttachment"
                />
              </div>
              <div
                v-if="questionGroups.length === 0"
                class="text-center text-medium-emphasis pa-8"
              >
                No questions configured for this assessment type.
              </div>
            </template>
            <VSkeletonLoader
              v-else
              type="card"
              height="200"
            />
          </VCardText>
        </VCard>

        <!-- Compliance Score Cards -->
        <ComplianceScoreCards
          v-if="hasComplianceQuestions"
          class="mt-4"
          :question-count="complianceQuestions.length"
          :avg-max-option-value="avgMaxOptionValue"
          :total-score="totalScore"
          :compliance-score="complianceScore"
          :compliance-score-formula="complianceScoreFormula"
          :compliance-percentage="compliancePercentage"
        />

        <!-- Navigation -->
        <VCard
          class="mt-6 border"
          variant="flat"
        >
          <VCardText>
            <div class="d-flex justify-space-between">
              <VBtn
                color="secondary"
                variant="tonal"
                @click="currentStep--"
              >
                <VIcon
                  icon="tabler-arrow-left"
                  start
                  class="flip-in-rtl"
                />
                Previous
              </VBtn>
              <VBtn
                color="primary"
                :loading="savingDraft"
                :disabled="savingDraft"
                @click="validateStep2"
              >
                Next
                <VIcon
                  icon="tabler-arrow-right"
                  end
                  class="flip-in-rtl"
                />
              </VBtn>
            </div>
          </VCardText>
        </VCard>
      </VWindowItem>

      <!-- STEP 3: Review & Submit -->
      <VWindowItem>
        <VCard
          variant="flat"
          class="border"
        >
          <VCardText>
            <div class="text-h5">
              Review & Submit
            </div>
            <span class="text-body-2">Check details and responses before final submission</span>
          </VCardText>

          <VCardText>
            <!-- Audit Details Review -->
            <div class="review-section pa-4 rounded-lg mb-4">
              <h6 class="text-subtitle-1 font-weight-medium mb-3 text-primary">
                Audit Details
              </h6>
              <div class="d-flex flex-column gap-2">
                <div class="d-flex align-center text-body-2">
                  <span class="review-label text-medium-emphasis">Audit:</span>
                  <span class="font-weight-medium">
                    {{ auditCollection?.audit?.title }}
                  </span>
                </div>
                <div class="d-flex align-center text-body-2">
                  <span class="review-label text-medium-emphasis">Assessment Type:</span>
                  <span class="font-weight-medium">
                    {{ selectedAssessmentTypeName }}
                  </span>
                </div>
                <div class="d-flex align-center text-body-2">
                  <span class="review-label text-medium-emphasis">Location:</span>
                  <span class="font-weight-medium">
                    {{ selectedLocationName }}
                  </span>
                </div>
                <div
                  v-if="isCustomerRelated && customerSummary"
                  class="d-flex align-center text-body-2"
                >
                  <span class="review-label text-medium-emphasis">{{ t('Customer') }}:</span>
                  <span class="font-weight-medium">
                    {{ customerSummary }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Q&A Preview -->
            <AppQAList
              :items="previewItems"
              title="Audit Responses"
              header-icon="tabler-clipboard-check"
              header-color="primary"
            />

            <!-- Compliance Score Cards -->
            <ComplianceScoreCards
              v-if="hasComplianceQuestions"
              class="mt-4"
              :question-count="complianceQuestions.length"
              :avg-max-option-value="avgMaxOptionValue"
              :total-score="totalScore"
              :compliance-score="complianceScore"
              :compliance-score-formula="complianceScoreFormula"
              :compliance-percentage="compliancePercentage"
            />
          </VCardText>
        </VCard>

        <!-- Action Buttons -->
        <VCard
          class="mt-6 border"
          variant="flat"
        >
          <VCardText>
            <div class="d-flex flex-wrap gap-4 justify-space-between">
              <VBtn
                color="secondary"
                variant="tonal"
                @click="currentStep--"
              >
                <VIcon
                  icon="tabler-arrow-left"
                  start
                  class="flip-in-rtl"
                />
                Previous
              </VBtn>
              <div class="d-flex gap-3">
                <VBtn
                  color="warning"
                  variant="tonal"
                  :loading="savingDraft"
                  @click="saveAsDraft"
                >
                  <VIcon
                    icon="tabler-device-floppy"
                    start
                  />
                  Save As Draft
                </VBtn>
                <VBtn
                  color="success"
                  variant="flat"
                  :loading="submitting"
                  @click="submitAudit"
                >
                  <VIcon
                    icon="tabler-send"
                    start
                  />
                  Submit
                </VBtn>
                <VBtn
                  v-if="isNonCompliant"
                  color="error"
                  variant="flat"
                  @click="openCapaDialog"
                >
                  <VIcon
                    icon="tabler-alert-triangle"
                    start
                  />
                  Submit & Raise CAPA
                </VBtn>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VWindowItem>
    </VWindow>

    <!-- CAPA Dialog for Non-Compliant Audit -->
    <VDialog
      v-model="isCapaDialogOpen"
      max-width="600"
      persistent
    >
      <VCard>
        <VCardItem>
          <template #title>
            <div class="d-flex align-center gap-2">
              <VIcon
                icon="tabler-alert-triangle"
                color="error"
              />
              <span class="text-h6 font-weight-medium">Non-Compliant Audit — Raise CAPA</span>
            </div>
          </template>
        </VCardItem>

        <VDivider />

        <VCardText>
          <div class="mb-4">
            <VLabel class="mb-2 text-body-2 text-high-emphasis">
              Failed Elements
            </VLabel>
            <div class="d-flex flex-wrap gap-2">
              <VChip
                v-for="element in failedElements"
                :key="element"
                color="error"
                variant="tonal"
                size="small"
                prepend-icon="tabler-x"
              >
                {{ element }}
              </VChip>
            </div>
          </div>

          <VForm ref="refCapaForm">
            <div class="d-flex flex-column gap-4">
              <AppTextarea
                v-model="capaForm.corrective_action"
                label="Corrective Action Taken"
                placeholder="Describe the corrective action taken"
                :rules="[requiredValidator]"
                required
                rows="3"
              />

              <DropZone
                title="Corrective Action Attachments"
                variant="compact"
                :preview="false"
                @on-change="data => capaForm.corrective_action_attachments.push(data)"
              />
              <div
                v-if="capaForm.corrective_action_attachments.length"
                class="d-flex flex-wrap gap-2"
              >
                <VChip
                  v-for="(item, index) in capaForm.corrective_action_attachments"
                  :key="index"
                  closable
                  size="small"
                  @click:close="capaForm.corrective_action_attachments.splice(index, 1)"
                >
                  {{ item.name ?? item.file?.name }}
                </VChip>
              </div>

              <AppTextarea
                v-model="capaForm.preventive_action"
                label="Preventive Action (Optional)"
                placeholder="Describe the preventive action"
                rows="3"
              />

              <DropZone
                title="Preventive Action Attachments"
                variant="compact"
                :preview="false"
                @on-change="data => capaForm.preventive_action_attachments.push(data)"
              />
              <div
                v-if="capaForm.preventive_action_attachments.length"
                class="d-flex flex-wrap gap-2"
              >
                <VChip
                  v-for="(item, index) in capaForm.preventive_action_attachments"
                  :key="index"
                  closable
                  size="small"
                  @click:close="capaForm.preventive_action_attachments.splice(index, 1)"
                >
                  {{ item.name ?? item.file?.name }}
                </VChip>
              </div>

              <AppDateTimePicker
                v-model="capaForm.preventive_action_due_date"
                label="Preventive Action Due Date (Optional)"
                placeholder="YYYY-MM-DD"
                :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
              />
            </div>
          </VForm>
        </VCardText>

        <VDivider />

        <VCardActions class="pa-4">
          <VSpacer />
          <VBtn
            color="secondary"
            variant="tonal"
            @click="isCapaDialogOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="error"
            variant="flat"
            :loading="submittingWithCapa"
            @click="submitWithCapa"
          >
            <VIcon
              icon="tabler-send"
              start
            />
            Submit & Raise CAPA
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>

  <!-- Loading state -->
  <section v-else>
    <div class="d-flex align-center gap-4 mb-6">
      <VSkeletonLoader
        type="avatar"
        width="48"
      />
      <div>
        <VSkeletonLoader
          type="text"
          width="200"
        />
        <VSkeletonLoader
          type="text"
          width="300"
        />
      </div>
    </div>
    <VSkeletonLoader type="card" />
  </section>
</template>

<style scoped>
.review-section {
  background-color: rgba(var(--v-theme-primary), 0.08);
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.review-label {
  inline-size: 140px;
  flex-shrink: 0;
}
</style>
